import React, { useState, useEffect } from "react";

import { Link, useFetcher, useRevalidator } from "@remix-run/react";
import { useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import {
    Circle,
    Calendar,
    User,
    FileText,
    CheckCircle,
    Bell,
} from "lucide-react";

import { EditableContent } from "~/components/editable-content";
import { GenericReminderDialog } from "~/components/reminders/reminder-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardFooter } from "~/components/ui/card";
import { P } from "~/components/ui/typography";
import { PartImage } from "~/features/parts/components/part-detail/part-image";
import { capitalizeFirstLetter, cn } from "~/lib/utils";

import { FulfillmentDialog } from "./fulfillment-dialog";
import type { CommitmentListItem } from "../types";

interface CommitmentsListProps {
    commitments: CommitmentListItem[];
    onCommitmentClick?: (commitment: CommitmentListItem) => void;
    emptyStateMessage?: string;
    showFulfillButton?: boolean;
    hideSessionFields?: boolean; // Hide session links when on session page
    hidePartName?: boolean; // Hide redundant part name (e.g. part detail page)
}

// AIDEV-NOTE: Custom hook for handling commitment content updates with optimistic UI
const useCommitmentContentUpdate = ({
    commitment,
    optimisticUpdates,
    setOptimisticUpdates,
    fetcher,
}: {
    commitment: CommitmentListItem;
    optimisticUpdates: Record<string, string>;
    setOptimisticUpdates: React.Dispatch<
        React.SetStateAction<Record<string, string>>
    >;
    fetcher: ReturnType<
        typeof useFetcher<{ error?: string; success?: boolean }>
    >;
}) => {
    // AIDEV-NOTE: Handle content update with optimistic updates to prevent flash
    const handleContentUpdate = async (value: string) => {
        // AIDEV-NOTE: Immediately update the optimistic state
        setOptimisticUpdates((prev) => ({
            ...prev,
            [commitment.id]: value,
        }));

        return new Promise<void>((resolve, reject) => {
            fetcher.submit(
                {
                    intent: "update-content",
                    commitmentId: commitment.id,
                    content: value,
                },
                {
                    method: "post",
                    action: "/commitments?index",
                    encType: "application/json",
                },
            );

            // AIDEV-NOTE: Wait for submission to complete by monitoring fetcher state
            const checkComplete = () => {
                if (fetcher.state === "idle") {
                    if (fetcher.data?.success) {
                        resolve();
                    } else if (fetcher.data?.error) {
                        // AIDEV-NOTE: Clear optimistic update on error
                        setOptimisticUpdates((prev) => {
                            const { [commitment.id]: _, ...rest } = prev;
                            return rest;
                        });
                        reject(new Error(fetcher.data.error));
                    }
                } else {
                    setTimeout(checkComplete, 100);
                }
            };
            checkComplete();
        });
    };

    // AIDEV-NOTE: Use optimistic value if available, otherwise use server value
    const displayContent =
        optimisticUpdates[commitment.id] ?? commitment.content;

    return { handleContentUpdate, displayContent };
};

// AIDEV-NOTE: Unified commitments list — commitments page, session-notes page, and the part
// detail page's left column. Minimal display everywhere (Jul 7 2026 — was mobile-only): just the
// date (no session title), the commitment text with the part thumbnail at LEFT, and compact
// Done | Dismiss | Remind CTAs.
export const CommitmentsList = ({
    commitments,
    onCommitmentClick,
    emptyStateMessage = "No commitments found",
    showFulfillButton = false,
    hideSessionFields = false,
    hidePartName = false,
}: CommitmentsListProps) => {
    const [selectedCommitment, setSelectedCommitment] =
        useState<CommitmentListItem | null>(null);
    const [isFulfillmentDialogOpen, setIsFulfillmentDialogOpen] =
        useState(false);

    // AIDEV-NOTE: Optimistic updates - track pending content changes by commitment ID
    const [optimisticUpdates, setOptimisticUpdates] = useState<
        Record<string, string>
    >({});

    const handleFulfillClick = (commitment: CommitmentListItem) => {
        setSelectedCommitment(commitment);
        setIsFulfillmentDialogOpen(true);
    };

    const fetcher = useFetcher<{ error?: string; success?: boolean }>();
    const queryClient = useQueryClient();
    const revalidator = useRevalidator();

    // AIDEV-NOTE: Track previous fetcher state to detect transitions (prevent infinite loop)
    const prevFetcherState = React.useRef(fetcher.state);

    // AIDEV-NOTE: Invalidate user details cache and trigger revalidation when archive/update action completes
    // Only trigger when fetcher TRANSITIONS from submitting->idle, not when already idle (prevents loop)
    useEffect(() => {
        if (
            fetcher.state === "idle" &&
            prevFetcherState.current === "submitting" &&
            fetcher.data?.success
        ) {
            queryClient.invalidateQueries({ queryKey: ["user"] });
            revalidator.revalidate();
        }
        prevFetcherState.current = fetcher.state;
    }, [fetcher.state, fetcher.data, queryClient, revalidator]);

    // AIDEV-NOTE: Clear optimistic updates when revalidation completes
    useEffect(() => {
        if (revalidator.state === "idle") {
            setOptimisticUpdates({});
        }
    }, [revalidator.state]);

    // Local helper to check if a reminder exists for a commitment (Phase 1: localStorage)
    const useHasReminder = (commitmentId: string) => {
        const [hasReminder, setHasReminder] = useState(false);

        const compute = () => {
            try {
                const raw = localStorage.getItem("reminders");
                if (!raw) return setHasReminder(false);
                const arr = JSON.parse(raw) as Array<{
                    type: string;
                    id: string;
                }>;
                setHasReminder(
                    Array.isArray(arr) &&
                        arr.some(
                            (r) =>
                                r.type === "commitment" &&
                                r.id === commitmentId,
                        ),
                );
            } catch {
                setHasReminder(false);
            }
        };

        // Compute on mount and when reminders update
        // Listen to both storage (cross-tab) and custom reminders-updated events
        React.useEffect(() => {
            compute();
            const handle = () => compute();
            window.addEventListener("storage", handle);
            window.addEventListener(
                "reminders-updated",
                handle as EventListener,
            );
            return () => {
                window.removeEventListener("storage", handle);
                window.removeEventListener(
                    "reminders-updated",
                    handle as EventListener,
                );
            };
        }, [commitmentId]);

        return { hasReminder, recompute: compute } as const;
    };

    const handleArchiveClick = (commitment: CommitmentListItem) => {
        fetcher.submit(
            {
                intent: "archive",
                commitmentId: commitment.id,
            },
            // AIDEV-NOTE: Use full action path to ensure it works from any page
            { method: "post", action: "/commitments?index" },
        );
    };

    if (commitments.length === 0) {
        return (
            <div className="py-12 text-center">
                <Circle className="text-muted-foreground mx-auto mb-4 size-12" />
                <p className="text-muted-foreground">{emptyStateMessage}</p>
            </div>
        );
    }

    const CommitmentItem = ({
        commitment,
    }: {
        commitment: CommitmentListItem;
    }) => {
        const partImage = commitment.part_interactions?.defaultImage;
        const { hasReminder, recompute } = useHasReminder(commitment.id);
        const hasPartImage =
            partImage &&
            (partImage.url ||
                ("signedUrl" in partImage && partImage.signedUrl));

        const { handleContentUpdate, displayContent } =
            useCommitmentContentUpdate({
                commitment,
                optimisticUpdates,
                setOptimisticUpdates,
                fetcher,
            });

        return (
            <Card
                variant="crafted"
                className="space-y-4 px-4 transition-shadow hover:shadow-md"
            >
                <CardContent className="space-y-2 p-0">
                    {/* AIDEV-NOTE: Minimal header (Jul 7 2026, all viewports) — just the DATE,
                        linked to the session; never the session title. */}
                    {!hideSessionFields && commitment.session?.title ? (
                        <div className="text-foreground mb-1 flex items-center gap-4 pt-4 text-sm">
                            <Link
                                to={`/sessions/${commitment.sessionId}`}
                                className="hover:text-foreground"
                            >
                                {commitment.displayDate}
                            </Link>
                        </div>
                    ) : null}
                    <div className="flex items-start gap-4">
                        {/* Content (optional part name + commitment text; wraps within the card) */}
                        <div className="min-w-0 flex-1">
                            {!hidePartName && (
                                <P className="mb-2">
                                    {commitment.partName ? (
                                        commitment.part_interactions?.part
                                            ?.id ? (
                                            <Link
                                                to={`/parts/${commitment.part_interactions.part.id}`}
                                                className="hover:text-foreground"
                                            >
                                                {commitment.partName}
                                            </Link>
                                        ) : (
                                            <span>{commitment.partName}</span>
                                        )
                                    ) : (
                                        commitment.participant + " Commitment"
                                    )}
                                </P>
                            )}
                            <div className="flex items-start justify-between gap-4">
                                <EditableContent
                                    defaultValue={displayContent}
                                    onSubmit={handleContentUpdate}
                                    className="font-medium"
                                    wrapperClassName="min-w-0 flex-1"
                                    padding={2}
                                />
                            </div>
                        </div>
                        {/* AIDEV-NOTE: Small part thumbnail at RIGHT (~2 text lines tall) on
                            all viewports, matching the session-notes part cards where the
                            image sits in the right column (Brent, 2026-09-09). */}
                        {hasPartImage && (
                            <div className="mt-1.5 shrink-0">
                                <div className="overflow-hidden rounded-lg">
                                    <PartImage image={partImage} size="xs" />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* AIDEV-NOTE: Action buttons sit below the image+text row so they
                        span the full card width (3-across on mobile) and aren't squeezed
                        into the narrow text column beside the thumbnail. */}
                    <div className={cn(!commitment.fulfilledAt && "pt-4")}>
                        {/* AIDEV-NOTE: Compact CTAs: Done | Dismiss | calendar icon — text
                            buttons keep their natural width (no flex-1) and carry no
                            icons; the reminder is an icon-only calendar button
                            (Brent, 2026-09-09). */}
                        {showFulfillButton && !commitment.fulfilledAt ? (
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() =>
                                        handleFulfillClick(commitment)
                                    }
                                >
                                    Done
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                        handleArchiveClick(commitment)
                                    }
                                    disabled={fetcher.state === "submitting"}
                                >
                                    Dismiss
                                </Button>
                                <GenericReminderDialog
                                    buttonVariant="ghost"
                                    buttonSize="icon"
                                    buttonClassName="size-9 shrink-0"
                                    triggerIcon="calendar"
                                    triggerLabel={null}
                                    triggerAriaLabel="Schedule a reminder"
                                    entityType="commitment"
                                    entityId={commitment.id}
                                    entityName={commitment.content}
                                    analyticsId="Schedule commitment reminder"
                                    description="Schedule a reminder to follow up on this commitment."
                                    onSaved={recompute}
                                />
                            </div>
                        ) : commitment.fulfilledAt ? null : ( // Fulfilled indicator now at top of card
                            <div className="flex items-center gap-2">
                                <GenericReminderDialog
                                    entityType="commitment"
                                    entityId={commitment.id}
                                    entityName={commitment.content}
                                    triggerLabel="Remind"
                                    analyticsId="Schedule commitment reminder"
                                    description="Schedule a reminder to follow up on this commitment."
                                    onSaved={recompute}
                                />
                            </div>
                        )}
                    </div>
                    {/* AIDEV-NOTE: Fulfilled section at bottom of card, below divider */}
                    {commitment.fulfilledAt && (
                        <div className="-mx-4 -mb-4 rounded-b-md border-t px-4 pt-4">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="size-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">
                                    Fulfilled on{" "}
                                    <span className="font-medium">
                                        {format(
                                            new Date(commitment.fulfilledAt),
                                            "MMMM d, yyyy",
                                        )}
                                    </span>
                                </span>
                            </div>
                            {commitment.fulfillmentText &&
                                commitment.fulfillmentText !== "undefined" && (
                                    <div className="mt-1.5 border-l-2 border-l-brand-forest-500 bg-brand-forest-50/30 py-1.5 pl-3.5 pr-3 rounded-r-md">
                                        <p className="text-foreground/75 text-sm italic leading-relaxed">
                                            {capitalizeFirstLetter(
                                                commitment.fulfillmentText,
                                            )}
                                        </p>
                                    </div>
                                )}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };

    return (
        <div className="space-y-3">
            {commitments.map((commitment) => (
                <div
                    key={commitment.id}
                    onClick={() => onCommitmentClick?.(commitment)}
                    className={onCommitmentClick ? "cursor-pointer" : ""}
                >
                    <CommitmentItem commitment={commitment} />
                </div>
            ))}

            {/* Fulfillment Dialog */}
            {showFulfillButton && (
                <FulfillmentDialog
                    isOpen={isFulfillmentDialogOpen}
                    onOpenChange={setIsFulfillmentDialogOpen}
                    commitment={selectedCommitment}
                    onSuccess={() => {
                        // The loader will refetch data automatically
                    }}
                />
            )}
        </div>
    );
};
